import { _ as _extends } from './extends-3e8335ef.esm.js';
import { a as AppIcon, A as AppText } from './index-495429ce.esm.js';
import PropTypes from 'prop-types';
import React__default, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { View, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { u as useTheme } from './useTheme-e658d3b6.esm.js';
import { A as AppInputLabel } from './index-bf5afcad.esm.js';
import { A as AppTextChip } from './index-aae7d5fc.esm.js';
import { A as AppActivityIndicator } from './index-5a30f9d1.esm.js';
import { A as AppAssets } from './index-fa849583.esm.js';
import MarkdownIt from 'markdown-it';

function validateEmail(input) {
  var validRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
  if (input?.match(validRegex)) {
    return true;
  } else {
    return false;
  }
}

let INPUT_TYPE = /*#__PURE__*/function (INPUT_TYPE) {
  INPUT_TYPE["EMAIL"] = "email";
  INPUT_TYPE["MENTION"] = "mention";
  INPUT_TYPE["SUBJECT"] = "subject";
  INPUT_TYPE["PROMPT"] = "prompt";
  return INPUT_TYPE;
}({});
function AppTextInput(_ref) {
  let {
    icon,
    autoCapitalise,
    onChangeText = () => {},
    onDeleteTagClick = () => {},
    onTagsChanged = () => {},
    onTagAdded = () => {},
    onRightIconClick,
    onLeftIconClick,
    value,
    placeholder,
    label,
    iconColor,
    isMandatory,
    width,
    showTextChips,
    style,
    topIcon,
    leftIcon,
    leftIconSize,
    topIconSize,
    rightIcon,
    rightIconSize,
    showTextInputLabel,
    wrapperInputStyle,
    backgroundColor,
    textContainer,
    tagsContainerStyle,
    inputType,
    isTagDeletable,
    isListDisplayedUnder = false,
    tagStyle,
    tags = [],
    forcedValidationError,
    isQueryInProgress,
    accessibilityLabel,
    multiline,
    textInputLabelStyle,
    maxCharacters,
    validationErrorStyle,
    rightIconStyle,
    rightIconLabel,
    defaultValue,
    hideShowIcon = false,
    isRightLabelFixed = false,
    mentioning = false,
    maxMentionsMenuHeight = 350,
    mentions = [],
    onMentionSelect = () => {},
    labelStyle,
    showMarkdownToggle = true,
    autoFocus = false,
    description,
    ...otherProps
  } = _ref;
  const {
    currentTheme
  } = useTheme();
  const [isMouseOver, setIsMouseOver] = useState(false);
  const [inputTags, setInputTags] = useState(tags);
  const [validationError, setValidationError] = useState(null);
  const [mentionListVisible, setMentionListVisible] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [isMarkdownRaw, setIsMarkdownRaw] = useState(true);
  const md = useRef(new MarkdownIt({
    html: true,
    linkify: true,
    typographer: true
  }));
  const [cursorPosition, setCursorPosition] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedMentions, setSelectedMentions] = useState([]);
  useRef(null);
  const isPositionWithinMention = position => {
    return selectedMentions.some(mention => position > mention.start && position <= mention.end);
  };
  const [showValue, setShowValue] = useState(false);
  const styles = generateStyle(currentTheme, leftIcon || rightIcon, isListDisplayedUnder);
  const {
    t
  } = useTranslation();
  const ref = useRef(null);
  useEffect(() => {
    if (autoFocus) {
      const timer = setTimeout(() => ref.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [autoFocus]);
  useEffect(() => {
    onTagsChanged(inputTags);
  }, [JSON.stringify(inputTags)]);
  useEffect(() => {
    setInputTags(tags);
  }, [tags?.length]);
  const validateInput = text => {
    switch (inputType) {
      case INPUT_TYPE.EMAIL:
        const isEmail = validateEmail(text);
        const isValueUnique = inputTags?.filter(u => u === text)?.length === 0;
        if (!isEmail) setValidationError("Invalid email address");
        if (!isValueUnique) setValidationError("The email address already exists");
        return isEmail && isValueUnique;
      default:
        return true;
    }
  };
  const onTextChipSubmit = text => {
    if (text !== "" && validateInput(text)) {
      onTagAdded(text);
      if (showTextChips) {
        inputTags.push(text);
        setInputTags([...inputTags]);
        onChangeText("");
      }
    }
  };
  const handleDeleteTagClick = item => {
    if (item) {
      onDeleteTagClick(item);
      var filtered = inputTags.filter(function (value) {
        return value !== item;
      });
      setInputTags([...filtered]);
    }
  };
  useEffect(() => {
    if (ref) {
      ref.current.setNativeProps({
        style: {
          fontFamily: "normalFont"
        }
      });
    }
  }, []);
  useEffect(() => {
    if (showMarkdownToggle && multiline) {
      const styleId = "markdown-text-input-styles";
      let styleEl = document.getElementById(styleId);
      if (!styleEl) {
        styleEl = document.createElement("style");
        styleEl.id = styleId;
        document.head.appendChild(styleEl);
      }
      styleEl.textContent = `
        .markdown-text-input-content {
          font-family: "normalFont", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          font-size: ${currentTheme?.theme?.small_font_size || 14}px;
          line-height: 1.5;
          color: ${currentTheme?.theme?.font_color || "#374151"};
        }
        .markdown-text-input-content strong,
        .markdown-text-input-content b {
          font-family: "boldFont", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          font-weight: bold;
        }
        .markdown-text-input-content em,
        .markdown-text-input-content i {
          font-style: italic;
        }
        .markdown-text-input-content code {
          font-family: monospace;
          background-color: ${currentTheme?.theme?.lightGrey || "#f3f4f6"};
          padding: 2px 6px;
          border-radius: 4px;
          font-size: ${currentTheme?.theme?.small_font_size || 14}px;
        }
        .markdown-text-input-content pre {
          background-color: ${currentTheme?.theme?.lightGrey || "#f3f4f6"};
          padding: 12px;
          border-radius: 6px;
          overflow-x: auto;
        }
        .markdown-text-input-content pre code {
          background-color: transparent;
          padding: 0;
        }
        .markdown-text-input-content h1,
        .markdown-text-input-content h2,
        .markdown-text-input-content h3,
        .markdown-text-input-content h4,
        .markdown-text-input-content h5,
        .markdown-text-input-content h6 {
          font-family: "boldFont", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          margin-top: 16px;
          margin-bottom: 8px;
        }
        .markdown-text-input-content p {
          margin: 8px 0;
        }
        .markdown-text-input-content ul,
        .markdown-text-input-content ol {
          margin: 8px 0;
          padding-left: 24px;
        }
        .markdown-text-input-content a {
          color: ${currentTheme?.theme?.primary || "#6366f1"};
          text-decoration: underline;
        }
        .markdown-text-input-content blockquote {
          border-left: 3px solid ${currentTheme?.theme?.primary || "#6366f1"};
          padding-left: 12px;
          margin: 8px 0;
          color: ${currentTheme?.theme?.darkGrey || "#6b7280"};
        }
      `;
      return () => {
        if (styleEl && styleEl.parentNode) {
          styleEl.parentNode.removeChild(styleEl);
        }
      };
    }
  }, [showMarkdownToggle, multiline, currentTheme]);
  const handleKeyPress = event => {
    switch (event.nativeEvent.key) {
      case "ArrowDown":
        event.preventDefault();
        setSelectedIndex(prev => prev < filteredMentions?.length - 1 ? prev + 1 : prev);
        break;
      case "ArrowUp":
        event.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case "Enter":
        if (mentioning) {
          event.preventDefault();
          if (filteredMentions[selectedIndex]) {
            handleMentionSelect(filteredMentions[selectedIndex]);
          }
        }
        break;
      case "Escape":
        setMentionListVisible(false);
        setSelectedIndex(0);
        break;
    }
  };
  const handleMentionSelect = mention => {
    const lastAtSymbolIndex = value.lastIndexOf("@", cursorPosition);
    const beforeMention = value.slice(0, lastAtSymbolIndex);
    const afterMention = value.slice(cursorPosition);
    const mentionText = String(mention.id); // Use ID instead of label
    const newText = `${beforeMention}@${mentionText} ${afterMention}`;

    // Add to selected mentions
    setSelectedMentions(prev => [...prev, {
      label: mentionText,
      // Store the ID as label for tracking
      start: lastAtSymbolIndex,
      end: lastAtSymbolIndex + mentionText.length + 1 // +1 for @ symbol
    }]);
    onChangeText?.(newText);
    onMentionSelect?.(mention);
    setMentionListVisible(false);
    setSelectedIndex(0);
  };
  const filteredMentions = mentions.filter(mention => {
    const query = mentionQuery?.toLowerCase() || "";
    return mention.label?.toLowerCase().includes(query) || mention.name?.toLowerCase().includes(query) || mention.description?.toLowerCase().includes(query);
  });
  return /*#__PURE__*/React__default.createElement(View, {
    style: [styles.wrapperInputStyle, wrapperInputStyle]
  }, showTextChips ? /*#__PURE__*/React__default.createElement(View, {
    style: [styles.tagsContainer, tagsContainerStyle]
  }, inputTags?.map((i, y) => /*#__PURE__*/React__default.createElement(AppTextChip, {
    styleContainer: tagStyle,
    isTagDeletable: isTagDeletable,
    key: y,
    onDeleteClick: handleDeleteTagClick,
    label: i
  }))) : null, showTextInputLabel ? /*#__PURE__*/React__default.createElement(AppInputLabel, {
    style: textInputLabelStyle,
    isMandatory: isMandatory,
    label: label
  }) : null, topIcon && /*#__PURE__*/React__default.createElement(AppIcon, {
    accessibilityLabel: `${accessibilityLabel}_top_icon`,
    color: iconColor,
    icon: topIcon,
    size: topIconSize || 24,
    style: {
      backgroundColor: currentTheme?.theme?.transparent
    }
  }), multiline ? /*#__PURE__*/React__default.createElement("div", {
    onMouseEnter: () => setIsMouseOver(true),
    onMouseLeave: () => setIsMouseOver(false)
  }, /*#__PURE__*/React__default.createElement(View, {
    style: [styles.textContainer, {
      width: width,
      backgroundColor: currentTheme?.theme?.inputBackgroundColor
    }, textContainer, {
      position: "relative"
    }]
  }, showMarkdownToggle && isMouseOver && /*#__PURE__*/React__default.createElement(View, {
    style: styles.markdownToggleContainer
  }, /*#__PURE__*/React__default.createElement(AppIcon, {
    isClickable: true,
    onPress: () => setIsMarkdownRaw(true),
    accessibilityLabel: "Raw markdown",
    icon: AppAssets?.icons?.md,
    size: 14,
    color: isMarkdownRaw ? currentTheme?.theme?.mediumGrey : currentTheme?.theme?.darkGrey,
    style: [styles.toggleIcon, isMarkdownRaw && styles.toggleIconActive]
  }), /*#__PURE__*/React__default.createElement(AppIcon, {
    isClickable: true,
    onPress: () => setIsMarkdownRaw(false),
    accessibilityLabel: "Formatted markdown",
    icon: AppAssets?.icons?.readMode,
    size: 14,
    color: !isMarkdownRaw ? currentTheme?.theme?.mediumGrey : currentTheme?.theme?.darkGrey,
    style: [styles.toggleIcon, !isMarkdownRaw && styles.toggleIconActive]
  })), isMarkdownRaw || !showMarkdownToggle ? /*#__PURE__*/React__default.createElement(TextInput, _extends({
    ref: ref,
    value: autoCapitalise ? value?.toUpperCase() : value,
    multiline: multiline
  }, otherProps, {
    accessibilityLabel: accessibilityLabel,
    onKeyPress: event => {
      if (mentioning && mentionListVisible && filteredMentions?.length > 0) {
        handleKeyPress(event);
      } else if (event?.nativeEvent.key === "Enter") onTextChipSubmit(value);
    },
    onSelectionChange: event => {
      setCursorPosition(event.nativeEvent.selection.start);
    },
    onChangeText: text => {
      text[cursorPosition - 1];
      const newChar = text.charAt(cursorPosition);
      const isAddingMention = newChar === "@";
      if (validationError) setValidationError(null);
      const lastAtSymbolIndex = text.lastIndexOf("@", cursorPosition);
      const textAfterAt = text.slice(lastAtSymbolIndex + 1, cursorPosition);
      const isInMentionContext = lastAtSymbolIndex !== -1 && !isPositionWithinMention(lastAtSymbolIndex);
      if (isAddingMention) {
        setMentionListVisible(true);
        setMentionQuery("");
        setSelectedIndex(0);
      } else if (isInMentionContext) {
        setMentionListVisible(true);
        setMentionQuery(textAfterAt);
      } else {
        setMentionListVisible(false);
      }
      onChangeText && onChangeText(autoCapitalise ? text?.toUpperCase() : text);
    },
    style: [styles.text, {
      width: "100%",
      height: "-webkit-fill-available",
      marginTop: 12
    }],
    placeholder: t(placeholder),
    placeholderTextColor: currentTheme?.theme?.inputPlaceholderColor
  })) : /*#__PURE__*/React__default.createElement("div", {
    dangerouslySetInnerHTML: {
      __html: md.current.render(value || "")
    },
    className: "markdown-text-input-content",
    style: {
      ...styles.markdownContent,
      marginTop: 4
    }
  }))) : /*#__PURE__*/React__default.createElement("div", {
    onMouseEnter: () => {
      setIsMouseOver(true);
    },
    onMouseLeave: () => {
      setIsMouseOver(false);
    },
    style: {
      cursor: "pointer"
    }
  }, /*#__PURE__*/React__default.createElement(View, {
    style: [styles.textContainer, {
      width: width,
      backgroundColor: currentTheme?.theme?.inputBackgroundColor
    }, textContainer]
  }, leftIcon && !isQueryInProgress && /*#__PURE__*/React__default.createElement(AppIcon, {
    isClickable: onLeftIconClick !== null,
    onPress: onLeftIconClick,
    accessibilityLabel: `${accessibilityLabel}_left_icon`,
    color: iconColor,
    icon: leftIcon,
    size: leftIconSize || 18,
    style: {
      backgroundColor: currentTheme?.theme?.transparent,
      flex: 0
    }
  }), /*#__PURE__*/React__default.createElement(AppActivityIndicator, {
    visible: isQueryInProgress,
    containerStyle: {
      alignItems: "flex-start",
      flex: 0,
      flexBasis: 24
    },
    size: currentTheme?.theme?.medium_font_size
  }), /*#__PURE__*/React__default.createElement(TextInput, _extends({
    ref: ref,
    secureTextEntry: hideShowIcon && !showValue,
    value: autoCapitalise ? value?.toUpperCase() : value
  }, otherProps, {
    accessibilityLabel: accessibilityLabel,
    onKeyPress: event => {
      if (event?.nativeEvent.key === "Enter") onTextChipSubmit(value);
    },
    onChangeText: text => {
      if (validationError) setValidationError(null);
      if (inputType === INPUT_TYPE.PROMPT) {
        if (text?.length === 0 || text?.length < maxCharacters) onChangeText(text);else {
          setValidationError("The text has reached the maximum length");
        }
      } else {
        onChangeText && onChangeText(autoCapitalise ? text?.toUpperCase() : text);
      }
    },
    style: [styles.text, style],
    placeholder: t(placeholder),
    placeholderTextColor: currentTheme?.theme?.inputPlaceholderColor
  })), hideShowIcon && /*#__PURE__*/React__default.createElement(AppIcon, {
    justifyHorizontally: true,
    labelStyle: {
      color: currentTheme?.theme?.primary
    },
    showToLeft: true,
    showToRight: false,
    label: "",
    accessibilityLabel: `${accessibilityLabel}_right_icon`,
    isClickable: true,
    onPress: () => {
      setShowValue(!showValue);
    },
    color: iconColor,
    icon: showValue ? AppAssets?.icons?.eyeSlash : AppAssets?.icons?.eye,
    size: rightIconSize || 18,
    wrapperStyle: {
      marginRight: 5,
      marginLeft: hideShowIcon && 15
    },
    style: [{
      backgroundColor: currentTheme?.theme?.transparent,
      flex: 0,
      width: "auto"
    }, rightIconStyle]
  }), rightIcon && /*#__PURE__*/React__default.createElement(AppIcon, {
    justifyHorizontally: true,
    labelStyle: [{
      color: currentTheme?.theme?.primary
    }, labelStyle],
    showToLeft: true,
    showToRight: false,
    label: (isMouseOver || isRightLabelFixed) && rightIconLabel,
    accessibilityLabel: `${accessibilityLabel}_right_icon`,
    isClickable: onRightIconClick !== null,
    onPress: () => {
      if (validateInput(value)) onRightIconClick && onRightIconClick(value);
    },
    color: iconColor,
    icon: rightIcon,
    size: rightIconSize || 18,
    style: [{
      backgroundColor: currentTheme?.theme?.transparent,
      flex: 0,
      width: "auto"
    }, rightIconStyle]
  }))), description ? /*#__PURE__*/React__default.createElement(AppText, {
    preventTranslation: true,
    leftIcon: AppAssets.icons.info,
    wrapperStyle: {
      marginBottom: 8,
      marginLeft: 2,
      marginTop: 5,
      gap: 5
    },
    color: currentTheme?.theme?.orange,
    iconSize: 12,
    style: {
      fontSize: 12
    }
  }, description) : null, (validationError || forcedValidationError) && /*#__PURE__*/React__default.createElement(AppText, {
    wrapperStyle: [{
      position: "absolute",
      top: -15,
      left: 26
    }, validationErrorStyle],
    color: currentTheme?.theme?.red,
    accessibilityLabel: `${accessibilityLabel}_error_text`
  }, validationError || forcedValidationError), mentionListVisible && /*#__PURE__*/React__default.createElement(View, {
    style: [styles.mentionList, {
      maxHeight: maxMentionsMenuHeight
    }],
    accessibilityRole: "list",
    accessibilityLabel: "Mention suggestions"
  }, filteredMentions.map((mention, index) => /*#__PURE__*/React__default.createElement(TouchableOpacity, {
    key: mention.id,
    onPress: () => handleMentionSelect(mention),
    style: [styles.mentionItem, index === selectedIndex && styles.selectedMentionItem],
    accessibilityRole: "button",
    accessibilityLabel: `Suggest ${mention.label}`,
    accessibilityState: {
      selected: index === selectedIndex
    }
  }, /*#__PURE__*/React__default.createElement(View, null, /*#__PURE__*/React__default.createElement(AppText, {
    style: [styles.mentionName, index === selectedIndex ? styles.selectedMentionText : undefined]
  }, mention.name || mention.label), mention.description && /*#__PURE__*/React__default.createElement(AppText, {
    style: [styles.mentionDescription, index === selectedIndex ? styles.selectedMentionDescriptionText : undefined]
  }, mention.description))))), maxCharacters && maxCharacters > 0 && /*#__PURE__*/React__default.createElement(View, {
    style: styles.characterCounterContainer
  }, /*#__PURE__*/React__default.createElement(AppText, {
    style: [styles.characterCounter, {
      color: value?.length >= maxCharacters ? currentTheme?.theme?.red : currentTheme?.theme?.darkGrey
    }]
  }, `${value?.length || 0}/${maxCharacters}`)));
}
const generateStyle = (props, isIconEmbedded, isListDisplayedUnder) => {
  return StyleSheet.create({
    wrapperInputStyle: {
      flexDirection: isListDisplayedUnder ? "column-reverse" : "column"
    },
    container: {
      display: "flex",
      flexDirection: "column"
    },
    textContainer: {
      margin: 5,
      flexDirection: "row",
      alignItems: "center",
      borderRadius: 10,
      borderColor: props?.theme?.border_color,
      borderWidth: 1,
      height: props?.theme?.fieldHeight,
      paddingRight: isIconEmbedded ? 20 : 15,
      paddingLeft: isIconEmbedded ? 5 : 15
    },
    text: {
      marginRight: isIconEmbedded ? 20 : 0,
      flex: 1,
      fontSize: props?.theme?.input_font_size || 14,
      color: props?.theme?.darkGrey,
      fontFamily: "normalFont",
      backgroundColor: props?.theme?.inputBackgroundColor
    },
    tagsContainer: {
      flexDirection: "row",
      flexWrap: "wrap"
    },
    mentionText: {
      color: "#2196F3",
      fontWeight: "500",
      backgroundColor: "rgba(33, 150, 243, 0.1)",
      borderRadius: 4,
      paddingHorizontal: 4,
      marginHorizontal: 2
    },
    highlightedMention: {
      color: props?.theme?.primary,
      // or your theme color
      fontWeight: "500"
    },
    selectedMentionItem: {
      backgroundColor: props?.theme?.primary_soft // light blue background for selected item
    },
    selectedMentionText: {
      color: props?.theme?.primary,
      fontWeight: "500"
    },
    mentionList: {
      position: "absolute",
      top: "100%",
      left: 0,
      right: 0,
      backgroundColor: props?.theme?.backgroundContrastColor,
      borderRadius: 4,
      elevation: 4,
      shadowColor: props?.theme?.lightGrey,
      shadowOffset: {
        width: 0,
        height: 2
      },
      shadowOpacity: 0.25,
      shadowRadius: 3.84,
      overflowY: "auto"
    },
    mentionItem: {
      padding: 10
    },
    mentionName: {
      fontSize: props?.theme?.small_font_size,
      fontWeight: "500",
      color: props?.theme?.darkGrey
    },
    mentionDescription: {
      fontSize: props?.theme?.xs_font_size || 12,
      color: props?.theme?.darkGrey,
      marginTop: 2
    },
    selectedMentionDescriptionText: {
      color: props?.theme?.primary,
      opacity: 0.8
    },
    characterCounterContainer: {
      position: "absolute",
      bottom: -10,
      right: 10,
      alignItems: "flex-start"
    },
    characterCounter: {
      fontSize: props?.theme?.extra_small_font_size || 12,
      fontFamily: "normalFont"
    },
    markdownToggleContainer: {
      position: "absolute",
      right: 5,
      top: 5,
      flexDirection: "row",
      zIndex: 10,
      backgroundColor: props?.theme?.lightGrey,
      borderRadius: 3,
      paddingRight: 3,
      paddingLeft: 0
    },
    toggleIcon: {
      marginLeft: 4,
      padding: 2,
      borderRadius: 4,
      backgroundColor: props?.theme?.transparent
    },
    toggleIconActive: {
      backgroundColor: props?.theme?.transparent
    },
    markdownContent: {
      width: "100%",
      height: "100%",
      marginTop: 10,
      paddingVertical: 0,
      color: props?.theme?.darkGrey,
      fontSize: props?.theme?.input_font_size || 14,
      fontFamily: "normalFont",
      overflowY: "auto"
    }
  });
};
AppTextInput.propsTypes = {
  placeholder: PropTypes.string
};
AppTextInput.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "AppTextInput",
  "props": {
    "onChangeText": {
      "defaultValue": {
        "value": "() => {}",
        "computed": false
      },
      "required": false,
      "tsType": {
        "name": "signature",
        "type": "function",
        "raw": "(text: string) => void",
        "signature": {
          "arguments": [{
            "name": "text",
            "type": {
              "name": "string"
            }
          }],
          "return": {
            "name": "void"
          }
        }
      },
      "description": ""
    },
    "onDeleteTagClick": {
      "defaultValue": {
        "value": "() => {}",
        "computed": false
      },
      "required": false,
      "tsType": {
        "name": "signature",
        "type": "function",
        "raw": "(item: any) => void",
        "signature": {
          "arguments": [{
            "name": "item",
            "type": {
              "name": "any"
            }
          }],
          "return": {
            "name": "void"
          }
        }
      },
      "description": ""
    },
    "onTagsChanged": {
      "defaultValue": {
        "value": "() => {}",
        "computed": false
      },
      "required": false,
      "tsType": {
        "name": "signature",
        "type": "function",
        "raw": "(item: any) => void",
        "signature": {
          "arguments": [{
            "name": "item",
            "type": {
              "name": "any"
            }
          }],
          "return": {
            "name": "void"
          }
        }
      },
      "description": ""
    },
    "onTagAdded": {
      "defaultValue": {
        "value": "() => {}",
        "computed": false
      },
      "required": false,
      "tsType": {
        "name": "signature",
        "type": "function",
        "raw": "(item: any) => void",
        "signature": {
          "arguments": [{
            "name": "item",
            "type": {
              "name": "any"
            }
          }],
          "return": {
            "name": "void"
          }
        }
      },
      "description": ""
    },
    "isListDisplayedUnder": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "required": false,
      "tsType": {
        "name": "boolean"
      },
      "description": ""
    },
    "tags": {
      "defaultValue": {
        "value": "[]",
        "computed": false
      },
      "required": false,
      "tsType": {
        "name": "Array",
        "elements": [{
          "name": "any"
        }],
        "raw": "any[]"
      },
      "description": ""
    },
    "hideShowIcon": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "required": false,
      "tsType": {
        "name": "boolean"
      },
      "description": ""
    },
    "isRightLabelFixed": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "required": false,
      "tsType": {
        "name": "boolean"
      },
      "description": ""
    },
    "mentioning": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "required": false,
      "tsType": {
        "name": "boolean"
      },
      "description": ""
    },
    "maxMentionsMenuHeight": {
      "defaultValue": {
        "value": "350",
        "computed": false
      },
      "required": false,
      "tsType": {
        "name": "number"
      },
      "description": ""
    },
    "mentions": {
      "defaultValue": {
        "value": "[]",
        "computed": false
      },
      "required": false,
      "tsType": {
        "name": "Array",
        "elements": [{
          "name": "signature",
          "type": "object",
          "raw": "{\n  id: string | number;\n  label: string;\n  name?: string;\n  description?: string;\n  // optional additional metadata\n  avatar?: string;\n  type?: string;\n}",
          "signature": {
            "properties": [{
              "key": "id",
              "value": {
                "name": "union",
                "raw": "string | number",
                "elements": [{
                  "name": "string"
                }, {
                  "name": "number"
                }],
                "required": true
              }
            }, {
              "key": "label",
              "value": {
                "name": "string",
                "required": true
              }
            }, {
              "key": "name",
              "value": {
                "name": "string",
                "required": false
              }
            }, {
              "key": "description",
              "value": {
                "name": "string",
                "required": false
              }
            }, {
              "key": "avatar",
              "value": {
                "name": "string",
                "required": false
              }
            }, {
              "key": "type",
              "value": {
                "name": "string",
                "required": false
              }
            }]
          }
        }],
        "raw": "Array<{\n  id: string | number;\n  label: string;\n  name?: string;\n  description?: string;\n  // optional additional metadata\n  avatar?: string;\n  type?: string;\n}>"
      },
      "description": ""
    },
    "onMentionSelect": {
      "defaultValue": {
        "value": "() => {}",
        "computed": false
      },
      "required": false,
      "tsType": {
        "name": "signature",
        "type": "function",
        "raw": "(mention: {\n  id: string | number;\n  label: string;\n  name?: string;\n  description?: string;\n}) => void",
        "signature": {
          "arguments": [{
            "name": "mention",
            "type": {
              "name": "signature",
              "type": "object",
              "raw": "{\n  id: string | number;\n  label: string;\n  name?: string;\n  description?: string;\n}",
              "signature": {
                "properties": [{
                  "key": "id",
                  "value": {
                    "name": "union",
                    "raw": "string | number",
                    "elements": [{
                      "name": "string"
                    }, {
                      "name": "number"
                    }],
                    "required": true
                  }
                }, {
                  "key": "label",
                  "value": {
                    "name": "string",
                    "required": true
                  }
                }, {
                  "key": "name",
                  "value": {
                    "name": "string",
                    "required": false
                  }
                }, {
                  "key": "description",
                  "value": {
                    "name": "string",
                    "required": false
                  }
                }]
              }
            }
          }],
          "return": {
            "name": "void"
          }
        }
      },
      "description": ""
    },
    "showMarkdownToggle": {
      "defaultValue": {
        "value": "true",
        "computed": false
      },
      "required": false,
      "tsType": {
        "name": "boolean"
      },
      "description": ""
    },
    "autoFocus": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "required": false,
      "tsType": {
        "name": "boolean"
      },
      "description": ""
    },
    "icon": {
      "required": false,
      "tsType": {
        "name": "IconDefinition"
      },
      "description": ""
    },
    "autoCapitalise": {
      "required": false,
      "tsType": {
        "name": "boolean"
      },
      "description": ""
    },
    "onRightIconClick": {
      "required": false,
      "tsType": {
        "name": "signature",
        "type": "function",
        "raw": "(item: any) => void",
        "signature": {
          "arguments": [{
            "name": "item",
            "type": {
              "name": "any"
            }
          }],
          "return": {
            "name": "void"
          }
        }
      },
      "description": ""
    },
    "onLeftIconClick": {
      "required": false,
      "tsType": {
        "name": "signature",
        "type": "function",
        "raw": "() => void",
        "signature": {
          "arguments": [],
          "return": {
            "name": "void"
          }
        }
      },
      "description": ""
    },
    "value": {
      "required": true,
      "tsType": {
        "name": "string"
      },
      "description": ""
    },
    "placeholder": {
      "required": false,
      "tsType": {
        "name": "string"
      },
      "description": ""
    },
    "label": {
      "required": false,
      "tsType": {
        "name": "string"
      },
      "description": ""
    },
    "isMandatory": {
      "required": false,
      "tsType": {
        "name": "boolean"
      },
      "description": ""
    },
    "width": {
      "required": false,
      "tsType": {
        "name": "number"
      },
      "description": ""
    },
    "showTextChips": {
      "required": false,
      "tsType": {
        "name": "boolean"
      },
      "description": ""
    },
    "style": {
      "required": false,
      "tsType": {
        "name": "StyleProp",
        "elements": [{
          "name": "ViewStyle"
        }],
        "raw": "StyleProp<ViewStyle>"
      },
      "description": ""
    },
    "topIcon": {
      "required": false,
      "tsType": {
        "name": "IconDefinition"
      },
      "description": ""
    },
    "leftIcon": {
      "required": false,
      "tsType": {
        "name": "IconDefinition"
      },
      "description": ""
    },
    "leftIconSize": {
      "required": false,
      "tsType": {
        "name": "number"
      },
      "description": ""
    },
    "topIconSize": {
      "required": false,
      "tsType": {
        "name": "number"
      },
      "description": ""
    },
    "rightIcon": {
      "required": false,
      "tsType": {
        "name": "IconDefinition"
      },
      "description": ""
    },
    "rightIconSize": {
      "required": false,
      "tsType": {
        "name": "number"
      },
      "description": ""
    },
    "showTextInputLabel": {
      "required": false,
      "tsType": {
        "name": "boolean"
      },
      "description": ""
    },
    "wrapperInputStyle": {
      "required": false,
      "tsType": {
        "name": "StyleProp",
        "elements": [{
          "name": "ViewStyle"
        }],
        "raw": "StyleProp<ViewStyle>"
      },
      "description": ""
    },
    "backgroundColor": {
      "required": false,
      "tsType": {
        "name": "string"
      },
      "description": ""
    },
    "textContainer": {
      "required": false,
      "tsType": {
        "name": "StyleProp",
        "elements": [{
          "name": "ViewStyle"
        }],
        "raw": "StyleProp<ViewStyle>"
      },
      "description": ""
    },
    "tagsContainerStyle": {
      "required": false,
      "tsType": {
        "name": "StyleProp",
        "elements": [{
          "name": "ViewStyle"
        }],
        "raw": "StyleProp<ViewStyle>"
      },
      "description": ""
    },
    "textInputLabelStyle": {
      "required": false,
      "tsType": {
        "name": "StyleProp",
        "elements": [{
          "name": "ViewStyle"
        }],
        "raw": "StyleProp<ViewStyle>"
      },
      "description": ""
    },
    "validationErrorStyle": {
      "required": false,
      "tsType": {
        "name": "StyleProp",
        "elements": [{
          "name": "ViewStyle"
        }],
        "raw": "StyleProp<ViewStyle>"
      },
      "description": ""
    },
    "labelStyle": {
      "required": false,
      "tsType": {
        "name": "StyleProp",
        "elements": [{
          "name": "ViewStyle"
        }],
        "raw": "StyleProp<ViewStyle>"
      },
      "description": ""
    },
    "rightIconStyle": {
      "required": false,
      "tsType": {
        "name": "StyleProp",
        "elements": [{
          "name": "ViewStyle"
        }],
        "raw": "StyleProp<ViewStyle>"
      },
      "description": ""
    },
    "tagStyle": {
      "required": false,
      "tsType": {
        "name": "StyleProp",
        "elements": [{
          "name": "ViewStyle"
        }],
        "raw": "StyleProp<ViewStyle>"
      },
      "description": ""
    },
    "inputType": {
      "required": false,
      "tsType": {
        "name": "INPUT_TYPE"
      },
      "description": ""
    },
    "isTagDeletable": {
      "required": false,
      "tsType": {
        "name": "boolean"
      },
      "description": ""
    },
    "multiline": {
      "required": false,
      "tsType": {
        "name": "boolean"
      },
      "description": ""
    },
    "iconColor": {
      "required": false,
      "tsType": {
        "name": "string"
      },
      "description": ""
    },
    "forcedValidationError": {
      "required": false,
      "tsType": {
        "name": "string"
      },
      "description": ""
    },
    "isQueryInProgress": {
      "required": false,
      "tsType": {
        "name": "boolean"
      },
      "description": ""
    },
    "accessibilityLabel": {
      "required": false,
      "tsType": {
        "name": "string"
      },
      "description": ""
    },
    "maxCharacters": {
      "required": false,
      "tsType": {
        "name": "number"
      },
      "description": ""
    },
    "rightIconLabel": {
      "required": false,
      "tsType": {
        "name": "string"
      },
      "description": ""
    },
    "defaultValue": {
      "required": false,
      "tsType": {
        "name": "string"
      },
      "description": ""
    },
    "description": {
      "required": false,
      "tsType": {
        "name": "string"
      },
      "description": ""
    }
  }
};

export { AppTextInput as A };
